/**
 * Helpers genéricos para construir reportes Excel — Fase 7.3
 *
 * Documentos relacionados:
 *   - DECISIONES.md #034 (stack reportes: exceljs cliente)
 *
 * Estrategia:
 *   - `buildReporteWorkbook({ titulo, subtitulo, columnas, rows, ... })`
 *     devuelve un `ExcelJS.Workbook` armado con la marca ATSA, listo para
 *     volcarse a buffer/blob.
 *   - `workbookToBlob(workbook)` lo convierte a Blob para descargar (async).
 *   - `generarReporteExcel(opts)` es el atajo más común: build + blob.
 *
 * Formato de columnas:
 *   - `currency`: `"$"#,##0.00`, alineación derecha
 *   - `integer`:  `#,##0`, alineación derecha
 *   - `date`:     `dd/mm/yyyy`, alineación centro
 *   - `text`:     plano, alineación izquierda
 */

import ExcelJS from 'exceljs';

// ── Tipos públicos ───────────────────────────────────────────────────────────

export type FormatoExcel = 'currency' | 'integer' | 'date' | 'text';
export type AlignExcel = 'left' | 'right' | 'center';

export interface ColumnaExcel {
  header: string;
  /** Key usada para mapear el valor en los rows (igual que ExcelJS.Column) */
  key: string;
  /** Ancho en "caracteres" (mismo concepto que Excel) */
  width?: number;
  /** Formato visual de la celda */
  format?: FormatoExcel;
  /** Override de alineación (si no, se infiere del formato) */
  align?: AlignExcel;
}

export interface BuildWorkbookOpts {
  titulo: string;
  subtitulo?: string;
  /** Nombre de la hoja (default: "Reporte") — Excel limita a 31 chars */
  nombreHoja?: string;
  columnas: ColumnaExcel[];
  /** Datos: objetos con keys que coinciden con `columnas[].key` */
  rows: Record<string, unknown>[];
  /** Fecha de generación (default: ahora) — visible en el header */
  generadoEn?: Date;
}

// ── Estilos de marca ATSA ────────────────────────────────────────────────────

const COLOR_PRIMARY_HEX = 'FF1565C0'; // ARGB: brand.palette.primary.main
const COLOR_HEADER_TEXT = 'FFFFFFFF';
const COLOR_TITULO_HEX = 'FF1565C0';
const COLOR_MUTED_HEX = 'FF616161';

const NUM_FMT_CURRENCY = '"$"#,##0.00';
const NUM_FMT_INTEGER = '#,##0';
const NUM_FMT_DATE = 'dd/mm/yyyy';

function alignFromFormato(formato?: FormatoExcel): AlignExcel {
  if (formato === 'currency' || formato === 'integer') return 'right';
  if (formato === 'date') return 'center';
  return 'left';
}

function numFmtFromFormato(formato?: FormatoExcel): string | undefined {
  if (formato === 'currency') return NUM_FMT_CURRENCY;
  if (formato === 'integer') return NUM_FMT_INTEGER;
  if (formato === 'date') return NUM_FMT_DATE;
  return undefined;
}

// ── API principal ────────────────────────────────────────────────────────────

/**
 * Construye un Workbook con la marca ATSA aplicada. NO genera blob —
 * para eso usar `workbookToBlob` o `generarReporteExcel`.
 *
 * Layout:
 *   - Fila 1: "ATSA Río Negro" (azul, bold, 14)
 *   - Fila 2: "Asociación de Trabajadores ..." (gris, 10)
 *   - Fila 3: titulo del reporte (negro, bold, 12)
 *   - Fila 4: subtitulo (gris, italic, 10) — si se pasa
 *   - Fila 5: "Generado: DD/MM/YYYY HH:mm" (gris, 9)
 *   - Fila 6: vacía
 *   - Fila 7: headers de columnas (fondo azul, blanco, bold)
 *   - Fila 8+: datos
 */
export function buildReporteWorkbook(opts: BuildWorkbookOpts): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'ATSA Río Negro - Boleta Sindical';
  wb.created = opts.generadoEn ?? new Date();

  const sheetName = (opts.nombreHoja ?? 'Reporte').slice(0, 31);
  const sheet = wb.addWorksheet(sheetName);

  // Configurar columnas (sin header todavía — lo agregamos manualmente
  // en una fila específica para poder poner el título arriba).
  sheet.columns = opts.columnas.map((c) => ({
    key: c.key,
    width: c.width ?? 18,
  }));

  // Fila 1: Marca
  const r1 = sheet.getRow(1);
  r1.getCell(1).value = 'ATSA Río Negro';
  r1.getCell(1).font = { name: 'Helvetica', size: 14, bold: true, color: { argb: COLOR_TITULO_HEX } };

  // Fila 2: Subtítulo de marca
  const r2 = sheet.getRow(2);
  r2.getCell(1).value = 'Asociación de Trabajadores de la Sanidad Argentina';
  r2.getCell(1).font = { name: 'Helvetica', size: 9, color: { argb: COLOR_MUTED_HEX } };

  // Fila 3: Título del reporte
  const r3 = sheet.getRow(3);
  r3.getCell(1).value = opts.titulo;
  r3.getCell(1).font = { name: 'Helvetica', size: 12, bold: true };

  // Fila 4: subtítulo del reporte (si lo hay)
  let headerRowIdx = 6;
  if (opts.subtitulo) {
    const r4 = sheet.getRow(4);
    r4.getCell(1).value = opts.subtitulo;
    r4.getCell(1).font = { name: 'Helvetica', size: 10, italic: true, color: { argb: COLOR_MUTED_HEX } };
    headerRowIdx = 7;
  }

  // Fila Generado:
  const rGen = sheet.getRow(opts.subtitulo ? 5 : 4);
  rGen.getCell(1).value = `Generado: ${(opts.generadoEn ?? new Date()).toLocaleString('es-AR')}`;
  rGen.getCell(1).font = { name: 'Helvetica', size: 8, color: { argb: COLOR_MUTED_HEX } };

  // Fila headers (azul + blanco + bold)
  const headerRow = sheet.getRow(headerRowIdx);
  opts.columnas.forEach((col, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = col.header;
    cell.font = { name: 'Helvetica', size: 10, bold: true, color: { argb: COLOR_HEADER_TEXT } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COLOR_PRIMARY_HEX },
    };
    cell.alignment = {
      horizontal: col.align ?? alignFromFormato(col.format),
      vertical: 'middle',
    };
    cell.border = {
      bottom: { style: 'thin', color: { argb: COLOR_PRIMARY_HEX } },
    };
  });
  headerRow.height = 22;

  // Filas de datos
  opts.rows.forEach((row) => {
    const dataRow = sheet.addRow(row);
    opts.columnas.forEach((col, idx) => {
      const cell = dataRow.getCell(idx + 1);
      const numFmt = numFmtFromFormato(col.format);
      if (numFmt) cell.numFmt = numFmt;
      cell.alignment = {
        horizontal: col.align ?? alignFromFormato(col.format),
        vertical: 'middle',
      };
      cell.font = { name: 'Helvetica', size: 10 };
    });
  });

  // Freeze: dejar visible el bloque del header al hacer scroll
  sheet.views = [{ state: 'frozen', ySplit: headerRowIdx }];

  return wb;
}

/**
 * Convierte un Workbook a Blob descargable (.xlsx).
 * Async porque exceljs serializa con stream.
 */
export async function workbookToBlob(wb: ExcelJS.Workbook): Promise<Blob> {
  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/**
 * Atajo: arma el workbook + lo convierte a Blob.
 * Es la API de uso normal desde los hooks de reportes.
 */
export async function generarReporteExcel(opts: BuildWorkbookOpts): Promise<Blob> {
  const wb = buildReporteWorkbook(opts);
  return workbookToBlob(wb);
}
